import {
  type AuthErrorCode,
  createAuthApiError,
} from '@/domains/auth/api/responseHelpers/error';
import { readErrorBody } from '@/utils/readErrorBody';
import { useMutation } from '@tanstack/react-query';
import z from 'zod';

const CLIENTID = process.env.NEXT_PUBLIC_CLIENT_ID || '';

// WAF block pages carry no API error code.
const wafErrorCodes: Partial<Record<number, AuthErrorCode>> = {
  403: 'AUTH/FORBIDDEN',
  429: 'AUTH/TOO-MANY-ATTEMPTS',
};

const loginResponseSchema = z.object({
  result: z.discriminatedUnion('status', [
    z.object({
      status: z.literal('SUCCESSFUL_LOGIN_NO_2FA'),
      firebaseCustomToken: z.string(),
      offer: z.string().nullish(),
      landingPageOffer: z.string().nullish(),
    }),
    z.object({
      status: z.literal('SUCCESSFUL_LOGIN_2FA'),
      firebaseCustomToken: z.string(),
      offer: z.string().nullish(),
      landingPageOffer: z.string().nullish(),
      trustToken: z.string().optional(),
    }),
    z.object({
      status: z.literal('OTP_SENT'),
      validatedMobilePhone: z.string(),
    }),
  ]),
});

type LoginResult = z.infer<typeof loginResponseSchema>['result'];

type Body = {
  email: string;
  password: string;
  otp?: string;
  landingPagePath?: string;
  trustDevice?: boolean;
  trustToken?: string;
};

export const useExchangeCredentialsForLoginTokenMutation = () => {
  const update = async (body: Body) => {
    const { email, password, otp, landingPagePath, trustDevice, trustToken } =
      body;
    let response: Awaited<ReturnType<typeof fetch>>;
    try {
      response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/auth/credential-login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'client-id': CLIENTID,
          },
          body: JSON.stringify({
            email,
            password,
            otp,
            landingPagePath,
            trustDevice,
            trustToken,
          }),
        },
      );
    } catch (error) {
      console.warn(error);
      throw new Error('Credential login request failed');
    }

    if (!response.ok) {
      throw createAuthApiError(
        response.status,
        await readErrorBody(response),
        wafErrorCodes,
      );
    }
    try {
      const data = await response.json();
      const parsed = loginResponseSchema.parse(data);
      const { result } = parsed;
      return result;
    } catch (error) {
      console.warn(error);
      throw new Error('Unexpected credential login response');
    }
  };
  return useMutation<LoginResult, unknown, Body>({
    mutationFn: update,
  });
};
